import { ActivityCode } from '@eppendorf/vnls-audit-event-client';
import { HasChildren } from '@eppendorf/vnls-react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCommitAuditEvent } from '$shared/audit-log';
import { AuthContext, AuthContextValue } from '$shared/auth/auth-context';
import {
  hasSession,
  onAuthEvent,
  signOut as authSignOut,
} from '$shared/auth/auth-service';
import { savePostLoginRedirectPath } from '$shared/auth/post-login-redirect.utils';
import { redirectToLogin } from '$shared/fetch';
import { createAuditEvent } from '$shared/utils/create-audit-event';

import { Loader } from '$components/loader/loader';

export function AuthContextProvider({ children }: HasChildren) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined);
  const { mutate: commit } = useCommitAuditEvent();

  const signOut = useCallback(async () => {
    setIsAuthenticated(undefined);
    try {
      const auditEvent = await createAuditEvent(ActivityCode.LOGOUT, {
        isCurrentUserAffected: true,
      });
      commit(auditEvent);
    } catch (error) {
      // eslint-disable-next-line no-console -- maybe useful for debugging
      console.error(error);
    }
    await authSignOut();
  }, []);
  useEffect(
    () =>
      onAuthEvent(async (event) => {
        try {
          if (event.type === 'signedIn') {
            const auditEvent = await createAuditEvent(ActivityCode.LOGIN, {
              isCurrentUserAffected: true,
            });
            commit(auditEvent);
          }

          // Amplify decodes and echoes back the `customState` we passed to
          // `signInWithRedirect()` as Cognito's OAuth `state` param once the Hosted UI
          // redirect completes. Storing it here is what makes the deep link survive the
          // redirect to Cognito and back - see post-login-redirect.utils.ts.
          if (event.type === 'customState') {
            savePostLoginRedirectPath(event.state);
          }

          // When Amplify detects a token refresh failure, it may automatically redirect
          // to the Hosted UI. Save the current path before that happens.
          // this was the leak we missed where we need to store a redirect path
          if (event.type === 'refreshFailed') {
            savePostLoginRedirectPath(
              `${window.location.pathname}${window.location.search}`,
            );
          }
        } catch (error) {
          // eslint-disable-next-line no-console -- maybe useful for debugging
          console.error(error);
        }
      }),
    [],
  );
  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    async function checkUserSession() {
      setIsAuthenticated(await hasSession());
    }

    checkUserSession().catch((error) => {
      // eslint-disable-next-line no-console -- maybe useful for debugging
      console.error(error);
    });
  }, []);
  const value: AuthContextValue = useMemo(
    () => ({
      isAuthenticated: !!isAuthenticated,
      isLoading: isAuthenticated === undefined,
      signIn: redirectToLogin,
      signOut,
    }),
    [isAuthenticated],
  );
  if (value.isLoading) {
    return <Loader />;
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
